import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CircleNotch, DownloadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ c, checked, site, sites, onToggle, onSite }) => (
  <div className={`grid grid-cols-[28px_1fr_120px] items-center gap-2 px-3 py-2 border-b border-[#F2F4F7] last:border-0 ${c.already_imported ? "opacity-55" : ""}`}
    data-testid={`machine-import-row-${c.resource_id}`}>
    <Checkbox checked={checked} disabled={c.already_imported} onCheckedChange={() => onToggle(c.resource_id)}
      data-testid={`machine-import-check-${c.resource_id}`} />
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[13px] font-bold text-[#101828]">{c.resource_id}</span>
        {c.already_imported && (
          <Badge variant="outline" className="text-[10px] whitespace-nowrap bg-[#ECFDF3] text-[#027A48] border-[#A6F4C5]"
            data-testid={`machine-import-done-${c.resource_id}`}>Already here</Badge>
        )}
        {!c.already_imported && !c.suggested_site && (
          <Badge variant="outline" className="text-[10px] whitespace-nowrap bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
            data-testid={`machine-import-needs-site-${c.resource_id}`}>Pick a site</Badge>
        )}
      </div>
      <p className="text-[12px] text-[#667085] truncate">{c.description || "no description in SAP"}</p>
    </div>
    <Select value={site || ""} onValueChange={(v) => onSite(c.resource_id, v)} disabled={c.already_imported}>
      <SelectTrigger className="h-8 text-xs" data-testid={`machine-import-site-${c.resource_id}`}>
        <SelectValue placeholder="Site" />
      </SelectTrigger>
      <SelectContent>
        {sites.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const MachineImportDialog = ({ open, onClose, onImported }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [workCenter, setWorkCenter] = useState("");
  const [picked, setPicked] = useState({});
  const [siteById, setSiteById] = useState({});
  const [showScheduling, setShowScheduling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: preview } = await axios.get(`${API}/machines/sap-import-preview`);
      setData(preview);
      setWorkCenter(preview.default_work_center || "");
      setPicked(Object.fromEntries((preview.candidates || [])
        .filter((c) => c.kind === "machine" && !c.already_imported && c.suggested_site)
        .map((c) => [c.resource_id, true])));
      setSiteById(Object.fromEntries((preview.candidates || []).map((c) => [c.resource_id, c.suggested_site || ""])));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the resource list from SAP");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const setSite = (id, site) => setSiteById((s) => ({ ...s, [id]: site }));

  const candidates = data?.candidates || [];
  const machines = candidates.filter((c) => c.kind === "machine");
  const scheduling = candidates.filter((c) => c.kind === "scheduling");
  const selected = candidates.filter((c) => picked[c.resource_id] && !c.already_imported);
  const missingSite = selected.filter((c) => !siteById[c.resource_id]);

  const runImport = async () => {
    setImporting(true);
    try {
      const { data: result } = await axios.post(`${API}/machines/sap-import`, {
        items: selected.map((c) => ({
          resource_id: c.resource_id, name: c.suggested_name, description: c.description,
          site_id: siteById[c.resource_id], work_center: workCenter,
        })),
        remember_work_center: workCenter,
      });
      const created = (result.created || []).length;
      if (created) toast.success(`${created} machine${created === 1 ? "" : "s"} created from SAP`);
      (result.failed || []).forEach((f) => toast.error(`${f.resource_id}: ${f.reason}`));
      if (!created && !(result.failed || []).length) toast.info("Nothing new to import");
      onImported();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[640px]" data-testid="machine-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Import machines from SAP</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-sm text-[#667085]" data-testid="machine-import-loading">
            <CircleNotch size={18} className="animate-spin inline mr-2" /> Reading equipment resources from SAP...
          </div>
        )}

        {!loading && data && !data.configured && (
          <p className="text-[13px] text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-lg px-3 py-2.5"
            data-testid="machine-import-unavailable">{data.note}</p>
        )}

        {!loading && data?.configured && (
          <div className="space-y-3">
            <p className="text-[12px] text-[#667085]">
              SAP holds <strong>{candidates.length}</strong> equipment resources. The <strong>{machines.length}</strong> physical
              machines are listed first - their name comes straight from SAP and their site from the ID (P1PP07 → P1).
              SAP has no work center on a resource, so pick one here; it becomes the default the nightly sync uses for
              machines it imports on its own.
            </p>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Work Center for everything imported now</Label>
              <Select value={workCenter} onValueChange={setWorkCenter}>
                <SelectTrigger data-testid="machine-import-work-center-select"><SelectValue placeholder="Select work center" /></SelectTrigger>
                <SelectContent>
                  {(data.work_centers || []).map((w) => (
                    <SelectItem key={w} value={w} data-testid={`machine-import-wc-option-${w}`}>{w}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border border-[#E4E7EC] rounded-lg max-h-[320px] overflow-y-auto">
              {machines.map((c) => (
                <Row key={c.resource_id} c={c} checked={!!picked[c.resource_id]} site={siteById[c.resource_id]}
                  sites={data.sites || []} onToggle={toggle} onSite={setSite} />
              ))}
              {scheduling.length > 0 && (
                <div className="bg-[#F9FAFB] border-t border-[#E4E7EC]">
                  <button onClick={() => setShowScheduling((v) => !v)}
                    className="w-full text-left px-3 py-2 text-[12px] text-[#475467] font-bold"
                    data-testid="machine-import-toggle-scheduling">
                    {showScheduling ? "Hide" : "Show"} {scheduling.length} scheduling resources (FG / SFG / RM - not machines)
                  </button>
                  {showScheduling && scheduling.map((c) => (
                    <Row key={c.resource_id} c={c} checked={!!picked[c.resource_id]} site={siteById[c.resource_id]}
                      sites={data.sites || []} onToggle={toggle} onSite={setSite} />
                  ))}
                </div>
              )}
            </div>

            {missingSite.length > 0 && (
              <p className="text-[12px] text-[#B54708] flex items-center gap-1.5" data-testid="machine-import-site-warning">
                <WarningCircle size={14} weight="fill" />
                {missingSite.map((c) => c.resource_id).join(", ")} need a site before they can be created.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="machine-import-cancel">Cancel</Button>
          <Button onClick={runImport} className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="machine-import-submit"
            disabled={importing || loading || !selected.length || !workCenter || missingSite.length > 0}>
            {importing ? <CircleNotch size={14} className="animate-spin" />
              : <><DownloadSimple size={14} weight="bold" className="mr-1.5" /> Import {selected.length || ""} machine{selected.length === 1 ? "" : "s"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SapMachineBanner = ({ pending, onImport }) => {
  if (!pending) return null;
  return (
    <div className="flex items-start gap-2 bg-[#ECFDF3] border border-[#A6F4C5] rounded-lg px-3 py-2.5 mb-5 text-[12px] text-[#027A48]"
      data-testid="machine-import-banner">
      <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
      <span className="flex-1">
        <strong>{pending}</strong> machine{pending === 1 ? "" : "s"} exist{pending === 1 ? "s" : ""} in SAP but not here yet.
        Import {pending === 1 ? "it" : "them"} with {pending === 1 ? "its" : "their"} SAP name and site - no retyping.
      </span>
      <button onClick={onImport} className="font-bold underline shrink-0" data-testid="machine-import-banner-btn">Import now</button>
    </div>
  );
};
